import {
  EvaluationType,
  FilterKeys,
  FilterSection,
  MARKBOOK_FILTER_KEYS,
  SavedFilter,
  SaveFilterResponse,
  useCreateFilterMutation,
  useGetFilterQuery,
  useRemoveFilterMutation,
  useUpdateFilterMutation,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { parseFiltersFromSearchParams } from '@schooly/components/filters';
import { useNotifications } from '@schooly/components/notifications';
import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from 'react';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form-lts';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';

import { filterToForm, formToFilter } from './markbookFilterConverters';

const MARKBOOK_PREVIEW_PATH = '/markbook/filters/preview';

export type MarkbookFilterForm = {
  id?: string;
  name: string;
  [FilterKeys.Date]: [string, string];
  [FilterKeys.AgeGroup]: string[];
  [FilterKeys.Subject]: string[];
  [FilterKeys.Group]: string[];
  [FilterKeys.Assessment]: string[];
  aol_evaluation_type: [string[], EvaluationType[]];
  // For internal use only
  selectedAssessmentIds: Set<string> | 'all';
  group_type: FilterKeys.Group | FilterKeys.AgeGroup;
  tutor_group: boolean;
};

export enum MarkbookFilterStep {
  SelectStudents = 1,
  SelectAssessments = 2,
  AdjustManually = 3,
}

export type MarkbookFilterPreview = Omit<SavedFilter, 'id'> & { type: 'new' | 'adjustment' };

interface MarkbookFilterContextValue {
  filterId?: string;
  isFilterLoading: boolean;

  filterPreview: MarkbookFilterPreview | null;
  setFilterPreview: (filterPreview: MarkbookFilterPreview) => void;
  clearFilterPreview: () => void;

  currentStep: MarkbookFilterStep;
  navigateToStep: (step: MarkbookFilterStep) => Promise<boolean>;
  showRecurring: boolean;
  onShowRecurringChange: (showRecurring: boolean) => void;

  form: UseFormReturn<MarkbookFilterForm>;
  filter?: SavedFilter;
  handleSaveFilter: () => Promise<SaveFilterResponse | undefined>;
  isUpdatingFilter: boolean;
  isRemovingFilter: boolean;
  handleRemoveFilter: () => Promise<void>;
  resetForm: () => Promise<void>;
}

const getDefaultFilter = (): Omit<MarkbookFilterForm, 'id'> => ({
  name: '',
  date: ['', ''],
  age_group: [],
  subject: [],
  group: [],
  assessment_ids: [],
  aol_evaluation_type: [[], []],
  selectedAssessmentIds: new Set([]),
  group_type: FilterKeys.AgeGroup,
  tutor_group: false,
});

// When landing on the preview path without a saved filter id, hydrate a
// MarkbookFilterPreview from URL search params so deep links and refreshes
// render the same markbook.
const buildMarkbookPreviewFromUrl = (
  searchParams: URLSearchParams,
  pathname: string,
  filterId: string | undefined,
): MarkbookFilterPreview | null => {
  if (filterId) return null;
  if (pathname !== MARKBOOK_PREVIEW_PATH) return null;

  const filter = parseFiltersFromSearchParams(searchParams, MARKBOOK_FILTER_KEYS);
  if (!Object.keys(filter).length) return null;

  return {
    name: '',
    section: FilterSection.Markbook,
    is_default: false,
    filter,
    type: 'new',
  };
};

const getMarkbookFormDefaults = (preview: MarkbookFilterPreview | null): MarkbookFilterForm => {
  const defaults = getDefaultFilter() as MarkbookFilterForm;
  if (!preview) return defaults;
  return { ...defaults, ...filterToForm({ ...preview.filter, name: '' }) } as MarkbookFilterForm;
};

const MarkbookFilterContext = createContext<MarkbookFilterContextValue | null>(null);

export const MarkbookFilterProvider: FC<PropsWithChildren> = ({ children }) => {
  const [showRecurring, setShowRecurring] = useState(false);
  const [currentStep, setCurrentStep] = useState(MarkbookFilterStep.SelectStudents);
  const { currentStaff, schoolId = '' } = useAuth();

  const { mutateAsync: createFilter, isLoading: isCreatingFilter } = useCreateFilterMutation();
  const { mutateAsync: updateFilter, isLoading: isUpdatingFilter } = useUpdateFilterMutation();
  const { mutateAsync: removeFilter, isLoading: isRemovingFilter } = useRemoveFilterMutation();
  const { showError } = useNotifications();

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  const [preview, setPreview] = useState<MarkbookFilterPreview | null>(() =>
    buildMarkbookPreviewFromUrl(searchParams, location.pathname, id),
  );

  const {
    isInitialLoading,
    isFetching,
    data: filter,
  } = useGetFilterQuery(id || '', {
    enabled: !!id,
    refetchOnMount: 'always',
    onSuccess: (data: SavedFilter) => {
      if (!preview) {
        form.reset(filterToForm({ ...data.filter, name: data.name }));
      }
    },
  });

  const form = useForm<MarkbookFilterForm>({
    defaultValues: getMarkbookFormDefaults(preview),
    mode: 'onChange',
  });

  const groupType = form.watch('group_type');

  const clearFilterPreview = useCallback(() => {
    setPreview(null);
  }, []);

  const validateCurrentStep = useCallback(async (): Promise<boolean> => {
    switch (currentStep) {
      case MarkbookFilterStep.SelectStudents:
        return await form.trigger(
          groupType === FilterKeys.AgeGroup ? ['date', 'age_group'] : ['date', 'group'],
        );
      case MarkbookFilterStep.SelectAssessments:
        return await form.trigger(['subject', 'aol_evaluation_type']);
      case MarkbookFilterStep.AdjustManually:
        return await form.trigger(['assessment_ids']);
      default:
        return false;
    }
  }, [currentStep, form, groupType]);

  const navigateToStep = useCallback(
    async (targetStep: MarkbookFilterStep): Promise<boolean> => {
      if (targetStep > currentStep) {
        const isValid = await validateCurrentStep();
        if (!isValid) {
          return false;
        }
      }

      setCurrentStep(targetStep);
      return true;
    },
    [currentStep, validateCurrentStep],
  );

  const setFilterPreview = useCallback((filterPreview: MarkbookFilterPreview) => {
    setPreview(filterPreview);
  }, []);

  const handleSaveFilter = useCallback(async (): Promise<SaveFilterResponse | undefined> => {
    if (!currentStaff) return;

    const formData = form.getValues();

    const { filter, ...rest } = formToFilter(formData, false);

    const requestParams = {
      relationId: currentStaff.relation_id,
      schoolId,
      ...rest,
      filter,
    };

    if (id) {
      return await updateFilter(
        {
          id,
          ...requestParams,
        },
        {
          onError: showError,
        },
      );
    } else {
      return await createFilter(requestParams, {
        onError: showError,
      });
    }
  }, [currentStaff, form, schoolId, id, updateFilter, showError, createFilter]);

  const handleRemoveFilter = useCallback(async () => {
    if (!id || !currentStaff) return;
    await removeFilter(
      { id, schoolId, relationId: currentStaff.relation_id },
      {
        onError: showError,
      },
    );
  }, [id, currentStaff, removeFilter, schoolId, showError]);

  const resetForm = useCallback(async () => {
    setCurrentStep(MarkbookFilterStep.SelectStudents);
    form.reset(getDefaultFilter());
  }, [form]);

  return (
    <FormProvider {...form}>
      <MarkbookFilterContext.Provider
        value={{
          filterPreview: preview,
          setFilterPreview,
          clearFilterPreview,
          filterId: id,
          currentStep,
          navigateToStep,
          form,
          showRecurring,
          onShowRecurringChange: setShowRecurring,
          isFilterLoading: isInitialLoading || isFetching,
          filter,
          handleSaveFilter,
          isUpdatingFilter: isUpdatingFilter || isCreatingFilter,
          handleRemoveFilter,
          isRemovingFilter,
          resetForm,
        }}
      >
        {children}
      </MarkbookFilterContext.Provider>
    </FormProvider>
  );
};

export const useMarkbookFilterContext = () => {
  const context = useContext(MarkbookFilterContext);
  if (!context) {
    throw new Error('useMarkbookFilterContext must be used within MarkbookFilterProvider');
  }
  return context;
};
